import { useMount } from 'ahooks';
import { Divider, Select, Space, TreeSelect } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { BUSINESS_TYPE_ENUM } from 'domains/gallerySkuDomain';
import {
  MultiTypeEnum,
  PartialSKU,
  SkuGalleryLabelsAll,
} from 'domains/skuDomain';
import useShowCompanyLabelFlag from 'hooks/useShowCompanyLabelFlag';
import { DataNode } from 'rc-tree-select/lib/interface';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { topGalleryLabels } from 'utils/review';

type Props = {
  sku: PartialSKU;
  updateCurItem: (item: PartialSKU) => Promise<void>;
  showTopGalleryLabelsError: boolean;
  showGalleryLabelError: boolean;
  showCompanyLabelError: boolean;
};

export const GalleryLabel = (props: Props) => {
  const {
    sku,
    updateCurItem,
    showTopGalleryLabelsError,
    showGalleryLabelError,
    showCompanyLabelError,
  } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [galleryLabels, setGalleryLabels] = useState<SkuGalleryLabelsAll>();
  const [loading, setLoading] = useState<boolean>(true);
  useMount(async () => {
    setLoading(true);

    const label = await dispatch.sku.getGalleryLabels();
    setGalleryLabels(label);
  });

  const galleryLabelTree = useMemo(() => {
    const cb = (isCompany: boolean): DataNode[] | [] => {
      return (
        (isCompany
          ? galleryLabels?.companyLabels
          : galleryLabels?.galleryLabels
        )?.map((item) => {
          return {
            value: item.id,
            title: item.name,
            children: item.dropDownLabels?.map((dItem) => {
              return {
                value: dItem.id,
                title: dItem.name,
              };
            }),
          };
        }) || []
      );
    };
    setTimeout(() => {
      setLoading(false);
    }, 1000);
    return cb;
  }, [galleryLabels]);

  return (
    <div
      className="single-sku-detail-form-row"
      hidden={sku.multiType === MultiTypeEnum.PART}
    >
      <Space>
        <label>{t('platform.sku.form.label.topGalleryLabels')}:</label>
        <div
          style={{
            position: 'relative',
          }}
        >
          <Select
            mode="multiple"
            style={{ minWidth: 200 }}
            showSearch
            disabled={sku.readonly}
            value={sku?.topGalleryLabels || undefined}
            filterOption={(input, option) =>
              (topGalleryLabels[option?.value as BUSINESS_TYPE_ENUM] || '')
                ?.toLowerCase()
                ?.includes(input?.toLowerCase())
            }
            onChange={(val) => {
              updateCurItem({
                ...sku,
                topGalleryLabels: val,
              });
            }}
            getPopupContainer={(e) => {
              return e as HTMLElement;
            }}
            dropdownStyle={{
              zIndex: 4,
            }}
          >
            {galleryLabels?.topGalleryLabels.map((galleryType) => {
              return (
                <Select.Option value={galleryType} key={galleryType}>
                  <span>{topGalleryLabels[galleryType]}</span>
                </Select.Option>
              );
            })}
          </Select>
          {Boolean(showTopGalleryLabelsError) && (
            <div
              className="ant-form-item-explain-error"
              style={{
                position: 'absolute',
                left: 0,
                top: 35,
              }}
            >
              {t('platform.sku.topGalleryLabelRequired')}
            </div>
          )}
        </div>
        <Divider type="vertical" />
        <label>{t('platform.sku.form.label.galleryLabel')}:</label>
        {Boolean(galleryLabelTree?.length && !loading) && (
          <div
            style={{
              position: 'relative',
            }}
          >
            <TreeSelect
              style={{ width: 300 }}
              showSearch
              disabled={sku.readonly}
              value={sku?.galleryLabel?.id}
              allowClear={true}
              treeDefaultExpandAll
              dropdownStyle={{
                zIndex: 4,
              }}
              filterTreeNode={(input, option) =>
                Boolean(
                  option?.title
                    ?.toLocaleString()
                    ?.toLowerCase()
                    ?.includes(input?.toLowerCase())
                )
              }
              onChange={(val) => {
                const payload = {
                  ...sku,
                  galleryLabelId: val,
                };
                delete payload.galleryLabel;
                updateCurItem(payload);
              }}
              treeData={galleryLabelTree(false)}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            />

            {Boolean(showGalleryLabelError) && (
              <div
                className="ant-form-item-explain-error"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 35,
                }}
              >
                {t('platform.sku.galleryLabelRequired')}
              </div>
            )}
          </div>
        )}
        {useShowCompanyLabelFlag() && (
          <>
            <Divider type="vertical" />
            <label>{t('platform.sku.form.label.companyLabel')}:</label>
            {Boolean(galleryLabelTree?.length && !loading) && (
              <div
                style={{
                  position: 'relative',
                }}
              >
                <TreeSelect
                  style={{ width: 300 }}
                  showSearch
                  disabled={sku.readonly}
                  value={sku?.companyLabel?.id}
                  treeDefaultExpandAll
                  dropdownStyle={{
                    zIndex: 4,
                  }}
                  filterTreeNode={(input, option) =>
                    Boolean(
                      option?.title
                        ?.toLocaleString()
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    )
                  }
                  onChange={(val) => {
                    updateCurItem({
                      ...sku,
                      companyLabelId: val,
                    });
                  }}
                  treeData={galleryLabelTree(true)}
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                />

                {Boolean(showCompanyLabelError) && (
                  <div
                    className="ant-form-item-explain-error"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 35,
                    }}
                  >
                    {t('platform.sku.galleryCompanyRequired')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Space>
    </div>
  );
};
